// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React from 'react';
import { clamp } from 'lodash';

import { Animation, type ColorName, Colors } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

/** @jsx isolateComponent */

type Props = {|
  /**
   * The color of the progress bar.
   */
  color: ColorName,
  /**
   * The maximum value for the progress bar.
   */
  max: number,
  /**
   * The minimum value for the progress bar.
   */
  min: number,
  /**
   * The amount of the progress bar. Should be between `min` and `max`.
   */
  value: number,
|};

const Styles = labelStyles(__filename, {
  root: () => [
    {
      position: 'relative',
      height: 5,
      borderRadius: 2.5,
      backgroundColor: Colors.grey200,
    },
  ],
  progress: (props: Props) => [
    {
      height: 'inherit',
      borderRadius: 'inherit',
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
    },
    withModifier(props.color, {
      backgroundColor: Colors[props.color],
    }),
  ],
});

/**
 * Basic progress component. Shows how full something is.
 *
 * Tags: core-ui
 */
const ProgressBar = (props: Props) => {
  const value = clamp(props.value, props.min, props.max);
  const range = props.max - props.min;
  const decimal = (value - props.min) / range;
  const width = `${decimal * 100}%`;
  return (
    <div css={Styles.root()}>
      <div
        css={Styles.progress(props)}
        data-testid="progress"
        style={{ width }}
      />
    </div>
  );
};

ProgressBar.defaultProps = {
  color: 'blue500',
  max: 1,
  min: 0,
};

export default ProgressBar;
